import * as Sentry from '@sentry/browser'

const getCookie = (cookieName) => {
  try {
    const cookies = decodeURIComponent(document.cookie).split(';')
    const parsedCookies = cookies.reduce((res, c) => {
      const splitted = c.split('=')
      if (splitted.length !== 2) {
        return res
      }

      const k = splitted[0].trim()
      const v = splitted[1].trim()
      res[k] = v
      return res
    }, {})

    if (!parsedCookies[cookieName]) return null

    // Safer parsing with fallback
    try {
      return JSON.parse(parsedCookies[cookieName])
    } catch (parseError) {
      Sentry.captureException(parseError, {
        tags: {
          module: 'cookieHandling',
          function: 'getCookie',
          cookieName,
        },
        extra: {
          cookieValue: parsedCookies[cookieName],
        },
      })
      return null // Return null instead of throwing
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'getCookie',
        cookieName,
      },
    })
    return null // Return null instead of throwing
  }
}

const setCookie = (cookieName, cookieValue, expiresInDays = 365) => {
  try {
    const expirationDate = new Date()
    expirationDate.setDate(expirationDate.getDate() + expiresInDays)
    const expires = 'expires=' + expirationDate.toUTCString()

    // Safely stringify value
    let valueToStore
    try {
      valueToStore = JSON.stringify(cookieValue)
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          module: 'cookieHandling',
          function: 'setCookie',
          cookieName,
        },
        extra: {
          cookieValueType: typeof cookieValue,
        },
      })
      valueToStore = JSON.stringify(['mandatory']) // Safe fallback
    }

    document.cookie = `${cookieName}=${valueToStore}; ${expires};path=/`
    return true
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'setCookie',
        cookieName,
      },
      extra: {
        expiresInDays,
      },
    })
    return false
  }
}

const CONSENT_COOKIE_NAME = 'cookieConsentChoice'

export const getConsentCookie = () => {
  try {
    const result = getCookie(CONSENT_COOKIE_NAME)
    return result
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'getConsentCookie',
      },
    })
    return null
  }
}

export const setConsentCookie = (value) => {
  try {
    return setCookie(CONSENT_COOKIE_NAME, value)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'setConsentCookie',
      },
    })
    return false
  }
}

// Diagnostic function to capture browser cookie environment information
// This will help diagnose issues with cookies in certain browsers
export const captureCookieEnvironmentInfo = () => {
  try {
    // Gather basic browser and cookie environment data
    const cookieData = {
      cookiesEnabled: navigator.cookieEnabled,
      cookieLength: document.cookie.length,
      hasConsentCookie: document.cookie.includes(CONSENT_COOKIE_NAME),
      userAgent: navigator.userAgent,
    }

    // Send this information to Sentry for debugging
    Sentry.setContext('cookieEnvironment', cookieData)

    return cookieData
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'captureCookieEnvironmentInfo',
      },
    })
    return null
  }
}
